/**
 * Tour definition helpers.
 *
 * A tour:
 *   id        unique key; also the i18n prefix (`tour.<id>.<step>_title|_body`)
 *   version   bump to show the tour again to people who already saw it
 *   scope     route prefix the tour belongs to (auto-starts on first visit)
 *   exact     match `scope` exactly instead of as a prefix
 *   appId     only offered when this app is installed (useInstalledApps)
 *   requires  optional ({ userdetails }) => boolean
 *   autoStart false to only run when started by hand
 *   steps     see `step()` below
 *
 * A step targets an element tagged `data-tour="<target>"` in the page. Keep
 * tours to 3–4 steps: title ≤ 4 words, body one plain sentence.
 */

/**
 * @param {string} tourId
 * @param {string} id        step id, used for i18n keys
 * @param {string|null} target  data-tour value, or null for a centred card
 * @param {React.ComponentType} icon
 * @param {object} [options]  placement ('top'|'bottom'|'left'|'right'), optional,
 *   route, mobile ('skip'), variant ('intro'), padding, beforeShow, onLeave, timeout
 */
export const step = (tourId, id, target, icon, options = {}) => ({
  id,
  target,
  icon,
  title: `tour.${tourId}.${id}_title`,
  body: `tour.${tourId}.${id}_body`,
  placement: 'bottom',
  ...options,
});

export const defineTour = ({ version = 1, ...tour }) => ({ version, ...tour });

export const OPTIONAL = { optional: true };

/**
 * Compact form used by the module files:
 *   moduleTour(id, scope, appId, [[stepId, target, icon, options], ...], extra)
 * `scope` is a route prefix (null for page tours); pass `{ page: 'x.y' }` in
 * `extra` for a tab that has no route of its own (see match.js).
 */
export const moduleTour = (id, scope, appId, steps, extra = {}) => defineTour({
  id, scope, appId, steps: steps.map((args) => step(id, ...args)), ...extra,
});
